// COMPONENTS & FUNCTIONS
import { calculatePipePressureDrop } from "../calculations/pipePressureDrop";

// DATA
import { Density } from "../baseQuantities/density";
import { Flow } from "../baseQuantities/flow";
import { Length } from "../baseQuantities/length";
import { NormalFlow } from "../baseQuantities/normalFlow";
import { Pressure } from "../baseQuantities/pressure";
import { SurfaceFinish } from "../baseQuantities/surfaceFinish";
import { UnitLess } from "../baseQuantities/unitLess";
import { Velocity } from "../baseQuantities/velocity";
import { Viscosity } from "../baseQuantities/viscosity";

// SVG & IMAGES
import pipePressureDropFigure from "../../img/calc-figure-pipe-pressure-drop.svg";

// INTERFACES
import {
  AdvancedCalculator,
  AppData,
} from "../../interface/functionalityInterface";

/*
PIPE PRESSURE DROP CALCULATOR INFORMATION
  Inputs and outputs are stored in coherent SI units before calculation.
  Liquid pressure drop is calculated with Darcy-Weisbach and the Haaland
  friction-factor approximation. The compressed-air calculation reproduces
  the empirical equation in PROC-CAL-0007 and uses normalized air flow with
  absolute inlet pressure. The model covers straight circular pipes only.
*/

const calculate = (inputs: Record<string, number>) =>
  calculatePipePressureDrop({
    innerDiameter: inputs.inner_diameter,
    pipeLength: inputs.pipe_length,
    roughness: inputs.pipe_roughness,
    liquidFlow: inputs.liquid_flow,
    density: inputs.liquid_density,
    viscosity: inputs.dynamic_viscosity,
    normalAirFlow: inputs.normal_air_flow,
    absoluteInletPressure: inputs.absolute_inlet_pressure,
  });

export const pipePressureDropCalc: AdvancedCalculator = {
  type: AppData.ADVANCED_CALCULATOR,
  definitionId: "pipe-pressure-drop",
  updatedAt: "2026-07-27",
  name: "pipe pressure drop",
  branch: "Fluids",
  difficulty: "complex",
  equation:
    "Liquid: Δp = f·(L/Di)·ρu²/2 | Air: Δp ∝ Qₙ¹·⁸⁵·L/(Di⁵·p₁)",
  figure: pipePressureDropFigure,
  inputs: [
    {
      ...Length,
      id: "pressure-drop-inner-diameter",
      name: "inner_diameter",
      type: "numeric",
      value: "35.6",
      clearValue: "",
      symbol: "Di",
      selectedUnit: "mm",
      section: "pipe",
    },
    {
      ...Length,
      id: "pressure-drop-length",
      name: "pipe_length",
      type: "numeric",
      value: "100",
      clearValue: "",
      symbol: "L",
      section: "pipe",
    },
    {
      ...SurfaceFinish,
      id: "pressure-drop-roughness",
      name: "pipe_roughness",
      type: "numeric",
      value: "50",
      clearValue: "",
      symbol: "ε",
      selectedUnit: "µm",
      section: "pipe",
    },
    {
      ...Flow,
      id: "pressure-drop-liquid-flow",
      name: "liquid_flow",
      value: "4",
      clearValue: "0",
      symbol: "Q",
      section: "liquid",
    },
    {
      ...Density,
      id: "pressure-drop-density",
      name: "liquid_density",
      type: "numeric",
      value: "998.2",
      clearValue: "",
      symbol: "ρ",
      section: "liquid",
    },
    {
      ...Viscosity,
      id: "pressure-drop-viscosity",
      name: "dynamic_viscosity",
      type: "numeric",
      value: "0.001",
      clearValue: "",
      symbol: "μ",
      section: "liquid",
    },
    {
      ...NormalFlow,
      id: "pressure-drop-normal-air-flow",
      name: "normal_air_flow",
      value: "300",
      clearValue: "0",
      symbol: "Qₙ",
      section: "compressed air",
    },
    {
      ...Pressure,
      id: "pressure-drop-inlet-pressure",
      name: "absolute_inlet_pressure",
      value: "5",
      clearValue: "",
      symbol: "p₁,a",
      selectedUnit: "bar",
      section: "compressed air",
    },
  ],
  outputs: [
    {
      ...Velocity,
      id: "pressure-drop-liquid-velocity",
      name: "liquid_velocity",
      decimalsOffset: 2,
      section: "liquid results",
    },
    {
      ...UnitLess,
      id: "pressure-drop-liquid-reynolds",
      name: "liquid_reynolds_number",
      section: "liquid results",
    },
    {
      ...UnitLess,
      id: "pressure-drop-liquid-friction-factor",
      name: "liquid_friction_factor",
      decimalsOffset: 4,
      section: "liquid results",
    },
    {
      ...Pressure,
      id: "pressure-drop-liquid-result",
      name: "liquid_pressure_drop",
      selectedUnit: "bar",
      decimalsOffset: 3,
      section: "liquid results",
    },
    {
      ...Pressure,
      id: "pressure-drop-air-result",
      name: "compressed_air_pressure_drop",
      selectedUnit: "bar",
      decimalsOffset: 3,
      section: "compressed air results",
    },
  ],
  calculate: (inputs) => {
    const result = calculate(inputs);
    if (!result) return null;

    const minimumDiameter = Math.max(
      inputs.inner_diameter * 0.35,
      0.001
    );
    const maximumDiameter = inputs.inner_diameter * 2.5;
    const diameterRange = maximumDiameter - minimumDiameter;
    const points = Array.from({ length: 101 }, (_, index) => {
      const innerDiameter =
        minimumDiameter + (diameterRange * index) / 100;
      const point = calculate({
        ...inputs,
        inner_diameter: innerDiameter,
      });
      return {
        inner_diameter: innerDiameter,
        liquid_pressure_drop: point?.liquidPressureDrop ?? NaN,
        compressed_air_pressure_drop:
          point?.compressedAirPressureDrop ?? NaN,
      };
    });

    return {
      outputs: {
        liquid_velocity: result.liquidVelocity,
        liquid_reynolds_number: result.liquidReynoldsNumber,
        liquid_friction_factor: result.liquidFrictionFactor,
        liquid_pressure_drop: result.liquidPressureDrop,
        compressed_air_pressure_drop:
          result.compressedAirPressureDrop,
      },
      series: points,
    };
  },
  validate: (inputs) => {
    const result = calculate(inputs);
    if (
      result &&
      inputs.normal_air_flow > 0 &&
      result.compressedAirPressureDrop >=
        inputs.absolute_inlet_pressure
    ) {
      return [
        "The compressed-air pressure drop is at least as large as the absolute inlet pressure.",
      ];
    }
    return [];
  },
  chart: {
    title: "Pressure drop over inner pipe diameter",
    xAxis: {
      key: "inner_diameter",
      label: "Inner diameter",
      baseUnit: Length.baseUnit,
      selectedUnit: "mm",
      selectedUnitFromInput: "inner_diameter",
      units: Length.units,
    },
    datasets: [
      {
        key: "liquid_pressure_drop",
        label: "Liquid pressure drop",
        axisId: "pressure",
        baseUnit: Pressure.baseUnit,
        selectedUnit: "bar",
        units: Pressure.units,
        borderColor: "#001e1d",
      },
      {
        key: "compressed_air_pressure_drop",
        label: "Compressed-air pressure drop",
        axisId: "pressure",
        baseUnit: Pressure.baseUnit,
        selectedUnit: "bar",
        units: Pressure.units,
        borderColor: "#777",
        borderDash: [5, 4],
      },
    ],
  },
};
